import { useEffect, useState } from "react";
import { Form, Radio, Checkbox, Input, Button } from "antd";
import {
  addUpdateCorporateDeclaration,
  getCorporateDeclarationInfo,
} from "../../../../api/request/corporate";
import { handleApiError } from "../../../../utils/handleApiError";
import toast from "react-hot-toast";

const CorporateDeclaration = ({
  onNext,
  onPrevious,
  isSubmitted,
  userId,
  isReviewMode = false,
}) => {
  const [form] = Form.useForm();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [declarationId, setDeclarationId] = useState(null);

  const isDisabled = isSubmitted;
  const isFieldDisabled = isDisabled && !isReviewMode;
  const principalsClean = Form.useWatch("confirmPrincipalsClean", form);

  const loadDeclarationInfo = async () => {
    try {
      if (!userId) return;
      const { data } = await getCorporateDeclarationInfo(userId);
      if (data?.success && data?.data) {
        if (data.data.id) setDeclarationId(data.data.id);
        form.setFieldsValue({
          confirmPrincipalsClean: data.data.confirmPrincipalsClean ?? true,
          principalsAdverseDetails: data.data.principalsAdverseDetails || "",
          confirmFundsNotFromMoneyLaundering:
            data.data.confirmFundsNotFromMoneyLaundering ?? false,
          agreeClientAgreement: data.data.agreeClientAgreement ?? false,
          confirmDetailsTrueAndNotifyChanges:
            data.data.confirmDetailsTrueAndNotifyChanges ?? false,
          confirmVoluntaryRegistration:
            data.data.confirmVoluntaryRegistration ?? false,
        });
      }
    } catch (error) {
      console.error("Error loading corporate declaration:", error);
    }
  };

  useEffect(() => {
    loadDeclarationInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleSubmit = async (values) => {
    if (isDisabled) return;
    try {
      setIsSubmitLoading(true);
      const payload = {
        userId,
        confirmPrincipalsClean: values.confirmPrincipalsClean,
        principalsAdverseDetails:
          values.confirmPrincipalsClean === false
            ? values.principalsAdverseDetails || ""
            : "",
        confirmFundsNotFromMoneyLaundering:
          values.confirmFundsNotFromMoneyLaundering,
        agreeClientAgreement: values.agreeClientAgreement,
        confirmDetailsTrueAndNotifyChanges:
          values.confirmDetailsTrueAndNotifyChanges,
        confirmVoluntaryRegistration: values.confirmVoluntaryRegistration,
      };

      if (declarationId) payload.id = declarationId;

      const { data } = await addUpdateCorporateDeclaration(payload);
      if (data?.success) {
        toast.success(data?.message || "Declaration saved successfully");
        onNext();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const requiredTrueRule = (message) => ({
    validator: (_, value) =>
      value ? Promise.resolve() : Promise.reject(new Error(message)),
  });

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Form
        form={form}
        layout="vertical"
        size="large"
        className={isReviewMode ? "corporate-review-readonly" : undefined}
        disabled={isFieldDisabled}
        onKeyDown={(event) => {
          if (!isReviewMode) return;
          if (event.target.closest?.(".corporate-review-nav")) return;
          event.preventDefault();
        }}
        onFinish={handleSubmit}
        initialValues={{ confirmPrincipalsClean: true }}
      >
        <div className="border border-gray-300 rounded-lg p-6 space-y-4">
          <h2 className="text-2xl font-semibold">
            Declaration & Acknowledgement
          </h2>

          <Form.Item
            name="confirmPrincipalsClean"
            label="Are the principals/directors of the company free from adverse regulatory or criminal history?"
            rules={[{ required: true, message: "Please select an option" }]}
          >
            <Radio.Group>
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </Form.Item>

          {principalsClean === false && (
            <Form.Item
              name="principalsAdverseDetails"
              label="Please provide details"
              rules={[
                {
                  required: true,
                  message: "Please provide adverse details",
                },
              ]}
            >
              <Input.TextArea rows={4} placeholder="Enter details" />
            </Form.Item>
          )}

          <Form.Item
            name="confirmFundsNotFromMoneyLaundering"
            valuePropName="checked"
            rules={[
              requiredTrueRule(
                "Please confirm funds are not from money laundering"
              ),
            ]}
          >
            <Checkbox>
              I confirm that the company's funds do not originate from money
              laundering or unlawful activities.
            </Checkbox>
          </Form.Item>

          <Form.Item
            name="agreeClientAgreement"
            valuePropName="checked"
            rules={[requiredTrueRule("Please agree to the client agreement")]}
          >
            <Checkbox>
              I agree to the Client Agreement and applicable terms.
            </Checkbox>
          </Form.Item>

          <Form.Item
            name="confirmDetailsTrueAndNotifyChanges"
            valuePropName="checked"
            rules={[
              requiredTrueRule(
                "Please confirm the details are true and agree to notify changes"
              ),
            ]}
          >
            <Checkbox>
              I confirm that the information provided is true and complete, and
              I undertake to notify UpForex of any changes.
            </Checkbox>
          </Form.Item>

          <Form.Item
            name="confirmVoluntaryRegistration"
            valuePropName="checked"
            rules={[
              requiredTrueRule("Please confirm voluntary registration"),
            ]}
          >
            <Checkbox>
              I confirm that I am registering voluntarily and of my own accord.
            </Checkbox>
          </Form.Item>
        </div>

        {isDisabled ? null : (
          <div className="flex justify-between mt-6">
            <Button onClick={onPrevious} size="large">
              Previous
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isSubmitLoading}
            >
              {isSubmitLoading ? "Saving..." : "Next"}
            </Button>
          </div>
        )}
      </Form>
      {isDisabled && (
        <div className="flex justify-between mt-6">
          <Button onClick={onPrevious} size="large">
            Previous
          </Button>
          <Button type="primary" size="large" onClick={onNext}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default CorporateDeclaration;
